import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import DesktopSeekSection from './DesktopSeekSection/DesktopSeekSection';
import TrackInfoScroller from '@/components/component-library/PlayerBar/TrackInfoScroller';
import { SMALL } from '@/config/assets';
import CurrentlyPlayingTrack from '@/components/app/CurrentlyPlayingTrack';
import AirplayIcon from '@/components/component-library/Icon/types/Airplay';
import QueueIcon from '@/components/component-library/Icon/types/Queue';
import HighVolumeIcon from '@/components/component-library/Icon/types/HighVolume';
import MidVolumeIcon from '@/components/component-library/Icon/types/MidVolume';
import NoVolumeIcon from '@/components/component-library/Icon/types/NoVolume';
import LayoutWrapperHorizontal from '@/components/component-library/LayoutWrapperHorizontal';
import MusicPlayerControls from '@/components/app/MusicPlayerControls';
import Overlay from '@/components/app/Overlay';
import ProgressBar from '@/components/app/ProgressBar';
import Queue from '@/components/app/Queue';
import SquareImage from '@/components/component-library/Image/KeakieImageSquare';
import useHeaderFooterHeights from '@/hooks/layout/useHeaderFooterHeights';
import { classes } from '@/helpers/styling';
import { isTabletIOS, isTouchDevice } from '@/helpers/browser';
import useSimpleAppState from '@/hooks/state/useSimpleAppState';

const MIN_VOLUME = 0;
const MAX_VOLUME = 100;

const MusicPlayerDesktop = ({
  episode,
  onPlayOrPause,
  onMoveEpisode,
  volume,
  muted,
  isOpen,
  onChangeVolume,
  onSetVolume,
  onSetMuted,
  onClickLibrary,
  libraryActionType,
  libraryActionLoading,
  airplayAvailable,
  onClickAirplay,
  onClickChannel,
  onClickEpisode,
  isFiltered,
  onShare,
  loading,
  libraryLoading
}) => {
  const { footerHeightDesktop } = useHeaderFooterHeights();
  const isAppleTablet = isTabletIOS();
  const touchDevice = isTouchDevice();

  const {
    title: episodeTitle,
    episode_number: episodeNumber,
    show
  } = episode || {};
  const { title: channelTitle, assets: imageAssets } = show || {};

  const [airplayHovered, setAirplayHovered] = useState(false);
  const [queueHovered, setQueueHovered] = useState(false);
  const { volumeBarOpen, setVolumeBarOpen } = useSimpleAppState();
  const [volumeChanging, setVolumeChanging] = useState(false);
  const [queueOpen, setQueueOpen] = useState(false);

  /**
   * Return a different icon based on audio volume
   *
   * @param {none}
   * @returns {string}
   */
  const getVolumeIcon = () => {
    if (muted || (volume === 0)) {
      return <NoVolumeIcon />;
    }
  
    if (volume > 0.75) {
      return <HighVolumeIcon />;
    }
  
    return <MidVolumeIcon />;
  };

  const handleSetHovered = useCallback((key, isHovered) => {
    if (key && !touchDevice) {
      if (key === 'queue') {
        setQueueHovered(isHovered);
      } else if (key === 'airplay') {
        setAirplayHovered(isHovered);
      } else if (key === 'volume') {
        setVolumeBarOpen(isHovered);
      }
    }
  }, [touchDevice, setVolumeBarOpen]);

  const handleChangeVolume = useCallback((vol) => {
    setVolumeChanging(false);
    onSetVolume({ vol: vol / 100 });
  }, [onSetVolume]);

  const handleMoveVolume = useCallback(({ seek: vol }) => {
    if (!volumeChanging) {
      setVolumeChanging(true);
    }

    const shouldMute = (vol <= MIN_VOLUME) && !muted;
    const shouldUnmute = (vol > MIN_VOLUME) && muted;

    if (shouldMute) {
      onSetMuted(true);
    } else if (shouldUnmute) {
      onSetMuted(false);
    }
    
    onChangeVolume({ vol: vol / 100 });
  }, [muted, onChangeVolume, onSetMuted, volumeChanging]);

  const handleMoveVolumeThrottled = (useCallback(throttle(handleMoveVolume, 100), [handleMoveVolume]));

  const handleMouseLeaveVolume = useCallback(() => {
    handleSetHovered('volume', false);
  }, [handleSetHovered]);

  const handleToggleQueue = useCallback(({ forceClose = false } = {}) => {
    if (forceClose) {
      setQueueOpen(false);
    } else {
      setQueueOpen((prev) => !prev);
    }
  }, []);

  // Avoid spamming of the queue toggle
  // Avoid the onBlur and clicking of the queue icon conflicting when closing the queue
  const handleToggleQueueThrottled = (useCallback(
    throttle(
      handleToggleQueue,
      350,
      {
        leading: true,
        trailing: false
      }
    ), [handleToggleQueue]
  ));

  const handleCloseQueue = useCallback(
    () => handleToggleQueueThrottled({ forceClose: true }),
    [handleToggleQueueThrottled]
  );

  const handleSpaceBarUp = useCallback((e) => {
    const { activeElement } = document;

    if (e.keyCode === 32 && onPlayOrPause) {
      e.preventDefault();

      if (
        !activeElement
        || (
          activeElement
          && !['input', 'select', 'button', 'textarea'].includes((activeElement.tagName || '').toLowerCase())
        )
      ) {
        onPlayOrPause(e);
      }
    }
  }, [onPlayOrPause]);

  const handleSpaceBarDown = useCallback((e) => {
    const { activeElement } = document;

    if (e.keyCode === 32) {
      if (
        !activeElement
        || (
          activeElement
          && !['input', 'select', 'button', 'textarea'].includes((activeElement.tagName || '').toLowerCase())
        )
      ) {
        e.preventDefault();
      }
    }
  }, []);

  const handleClickMusicBar = useCallback((e) => e.preventDefault(), []);
  const handleMouseEnterQueueIcon = useCallback(() => handleSetHovered('queue', true), [handleSetHovered]);
  const handleMouseLeaveQueueIcon = useCallback(() => handleSetHovered('queue', false), [handleSetHovered]);
  const handleMouseOverVolume = useCallback(() => handleSetHovered('volume', true), [handleSetHovered]);
  const handleClickVolumeIcon = useCallback(() => onSetMuted(!muted), [muted, onSetMuted]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keyup', handleSpaceBarUp);
      window.addEventListener('keydown', handleSpaceBarDown);
    }

    return () => {
      window.removeEventListener('keyup', handleSpaceBarUp);
      window.removeEventListener('keydown', handleSpaceBarDown);
    };
  }, [handleSpaceBarUp, handleSpaceBarDown, isOpen]);

  const commonStyle = useMemo(() => ({
    transform: `translateY(-${ footerHeightDesktop }px)`,
    height: 'unset'
  }), [footerHeightDesktop]);

  const musicBarStyle = useMemo(() => ({
    height: `${ footerHeightDesktop }px`,
    paddingBottom: isAppleTablet ? '32px' : '12px'
  }), [footerHeightDesktop, isAppleTablet]);

  return (
    <>
      <Overlay
        className='track-listing-desktop-content'
        commonClassName='track-listing-desktop'
        commonStyle={ commonStyle }
        zIndex={ 60 }
      >
        <LayoutWrapperHorizontal
          className='currently-playing-track-wrapper'
          isRow
        >
          <CurrentlyPlayingTrack className='currently-playing-track-desktop' isOpen={ isOpen } />
        </LayoutWrapperHorizontal>
      </Overlay>
      <div
        className={ classes('player-bar', 'player-bar-desktop', {
          '--is-hidden': !isOpen,
          '--is-filtered': isFiltered
        }) }
        data-nosnippet=''
        onClick={ handleClickMusicBar }
        style={ musicBarStyle }
      >
        <LayoutWrapperHorizontal isRow>
          <div className={ classes('track-wrapper', { '--is-visible': episode && episode.id }) }>
            <div className='player-bar-image-wrapper'>
              <SquareImage
                assetSize={ SMALL }
                assetsObj={ imageAssets }
                key={ episode.id }
                onClick={ onClickChannel }
              />
            </div>
            <TrackInfoScroller
              channelTitle={ channelTitle }
              episodeNumber={ episodeNumber }
              episodeTitle={ episodeTitle }
              libraryActionLoading={ libraryActionLoading }
              libraryActionType={ libraryActionType }
              libraryLoading={ libraryLoading }
              onClickChannel={ onClickChannel }
              onClickEpisode={ onClickEpisode }
              onClickLibrary={ onClickLibrary }
              onClickShare={ onShare }
            />
          </div>
          <div className='controls-wrapper'>
            <MusicPlayerControls
              className='controls'
              loading={ loading }
              minus15ClassName='minus-15'
              nextClassName='next'
              playClassName='play'
              plus15ClassName='plus-15'
              previousClassName='previous'
            />
            <DesktopSeekSection />
          </div>
          <div className='sub-controls-wrapper'>
            <div
              className={ classes('airplay-wrapper', { '--is-hidden': !airplayAvailable }) }
              onClick={ onClickAirplay }
              onMouseEnter={ airplayAvailable ? handleMouseEnterQueueIcon : undefined }
              onMouseLeave={ airplayAvailable ? handleMouseLeaveQueueIcon : undefined }
            >
              <AirplayIcon
                className={ classes('airplay-icon', { '--is-faded': volumeBarOpen || queueHovered }) }
              />
            </div>
            { !touchDevice && (
              <div
                className='volume-wrapper'
                onMouseLeave={ handleMouseLeaveVolume }
                onMouseOver={ handleMouseOverVolume }
              >
                <div
                  className={ classes('volume-icon', {
                    '--is-faded': airplayHovered || queueHovered,
                    '--is-muted': muted
                  }) }
                >
                  { getVolumeIcon() }
                  <div
                    className='volume-icon-click'
                    onClick={ handleClickVolumeIcon }
                  />
                  <div className={ classes('desktop-volume-bar-wrapper', { '--is-visible': volumeBarOpen || volumeChanging }) }>
                    <ProgressBar
                      alwaysShowThumb
                      className='desktop-volume-bar'
                      forceStartPosition={ muted }
                      max={ MAX_VOLUME }
                      onMoveSlider={ handleMoveVolumeThrottled }
                      onSeek={ handleChangeVolume }
                      seek={ volume * 100 }
                      sliderProgressClassName='desktop-volume-progress'
                      vertical
                    />
                  </div>
                </div>
              </div>
            ) }
            <div
              className='queue-wrapper'
              onClick={ handleToggleQueueThrottled }
              onMouseEnter={ handleMouseEnterQueueIcon }
              onMouseLeave={ handleMouseLeaveQueueIcon }
            >
              <QueueIcon
                className={ classes('queue-icon', {
                  '--is-open': queueOpen,
                  '--is-faded': airplayHovered || volumeBarOpen
                }) }
              />
            </div>
          </div>
          { isOpen && (
            <Queue
              isDesktop
              onClose={ handleCloseQueue }
              onMoveEpisode={ onMoveEpisode }
              open={ queueOpen }
            />
          ) }
        </LayoutWrapperHorizontal>
      </div>
    </>
  );
};

MusicPlayerDesktop.propTypes = {
  episode: PropTypes.object.isRequired,
  onPlayOrPause: PropTypes.func,
  onMoveEpisode: PropTypes.func.isRequired,
  volume: PropTypes.number.isRequired,
  muted: PropTypes.bool,
  isOpen: PropTypes.bool,
  onChangeVolume: PropTypes.func.isRequired,
  onSetVolume: PropTypes.func.isRequired,
  onSetMuted: PropTypes.func.isRequired,
  onClickLibrary: PropTypes.func,
  libraryActionType: PropTypes.string,
  libraryActionLoading: PropTypes.bool,
  airplayAvailable: PropTypes.bool,
  onClickAirplay: PropTypes.func,
  onClickChannel: PropTypes.func.isRequired,
  onClickEpisode: PropTypes.func.isRequired,
  isFiltered: PropTypes.bool,
  onShare: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  libraryLoading: PropTypes.bool
};

MusicPlayerDesktop.defaultProps = {
  muted: false,
  onPlayOrPause: undefined,
  onClickLibrary: undefined,
  onClickAirplay: undefined,
  isOpen: false,
  libraryActionType: undefined,
  libraryActionLoading: false,
  airplayAvailable: false,
  isFiltered: false,
  loading: false,
  libraryLoading: false
};

export default MusicPlayerDesktop;